import { HistoryOrderItem } from './../models/cartModels';
import { DateTime } from 'luxon';
import { FullOrder, OrderConfirmation, ItemPriceCal, OrderLogistics, HistoryOrder } from '@/models/cartModels';
import { ToppingPrice } from './../models/menuModels';
import ApiHelper from '@/services/ApiHelper';
import { AvailableTopping, Menu, Topping, ToppingReq, MenuItem, Size, Recipe, PizzaToppingOption, TwoSidedSelection } from '@/models/menuModels';
import { objectToQueryString, phoenixError, scrubHtmlElements } from './stringService';
import { LineItem, AddToCartRes, Coupon } from '@/models/cartModels';
import { GiftCard, validGiftCardResponse } from '@/models/PaymentModels';
import { useGoogleAnalytics } from '@/composables/useGoogleAnalytics';
import { CustomMenuItemNameEnum } from '@/enums/CustomMenuItemNameEnum';
import { BAKERY_PIZZA_TOPPING_LIMIT, CALZATO_TOPPING_LIMIT, PIZZA_TOPPING_LIMIT } from '@/constants';
import { PizzaSideEnum } from '@/enums/PizzaSideEnum';

export const removeDuplicateToppings = (toppings: Topping[]) => toppings?.filter((value, index, self) => index === self.findIndex((t) => t.toppingPid === value.toppingPid));

export const simpleLineItemMapper = (menuItem: MenuItem, quantity: number, availableTopping: AvailableTopping) => {
    //@ts-ignore
    return new LineItem({
        customerId: undefined,
        quantity: quantity,
        id: menuItem?.itemExternalId,
        name: menuItem?.name,
        category: menuItem.categoryName,
        baseprice: menuItem?.itemPrice,
        imageSm: menuItem?.itemImage,
        price: menuItem.itemPrice,
        selectedInstructions: [],
        recipes: menuItem.firstRecipe?.recipeLineItem([menuItem.firstSize]),
        toppings: availableTopping.toppings,
        selectedToppings: removeDuplicateToppings(availableTopping.updatingSelectedToppings ?? availableTopping.toppings?.filter((t) => t.selected)),
        selectedDressings: availableTopping.dressings,
        selectedFlavors: availableTopping.flavors,
        selectedSauces: [],
    });
};

export const mapMenuItemToLineItem = (menuItem: MenuItem, size: Size, recipe: Recipe, availableTopping: AvailableTopping): LineItem => {
    const price = size?.price ?? menuItem?.itemPrice;
    //@ts-ignore
    return new LineItem({
        quantity: 1,
        id: menuItem?.itemExternalId,
        name: menuItem?.name,
        category: menuItem?.categoryName,
        baseprice: menuItem?.itemPrice,
        imageSm: menuItem?.itemImage,
        price: price,
        selectedInstructions: [],
        recipes: size ? recipe?.recipeLineItem([size]) : [],
        toppings: availableTopping.toppings,
        selectedToppings: removeDuplicateToppings(availableTopping.toppings?.filter((t) => t.selected)),
        selectedRequiredToppings: (recipe?.isStuffedCrust || recipe?.isBakery) && availableTopping?.dressings.length > 0 ? availableTopping?.dressings : [],
        selectedDressings: [],
        selectedFlavors: [],
        selectedSauces: [],
        bundleIdentifier: menuItem?.bundleIdentifier,
    });
};

export const isPizzaCoverageValid = (toppings: Topping[] | undefined): boolean => {
    if (!toppings?.length) {
        return false;
    }

    const toppingPlacements = toppings.map((t) => Number(t.placement));

    if (toppingPlacements.includes(3)) {
        return true;
    }

    return toppingPlacements.includes(1) && toppingPlacements.includes(2);
};

export const countToppings = (toppings: ReadonlyArray<{ multiplier?: number }> | undefined): number => (toppings ?? []).reduce((total, t) => total + (t.multiplier ?? 0), 0);

export const getToppingLimit = (currentMenuItem: MenuItem | null): number => {
    if (currentMenuItem?.isCalzato) return CALZATO_TOPPING_LIMIT;
    if (currentMenuItem?.isBakery) return BAKERY_PIZZA_TOPPING_LIMIT;
    return PIZZA_TOPPING_LIMIT;
};

export const getOverLimitMessage = (currentMenuItem: MenuItem | null): string =>
    `${getToppingLimit(currentMenuItem)} toppings is the max.\nLove the enthusiasm, but nobody wants a soggy pizza/calzato/etc.\nFYI: extra toppings count as 2.`;

export const isToppingLimitReached = (toppings: Topping[] | undefined, currentMenuItem: MenuItem | null): boolean => countToppings(toppings) > getToppingLimit(currentMenuItem);

export const isHalfSigToppingsValid = (twoSidedLineItem: LineItem): boolean => {
    let allValid = true;
    if (twoSidedLineItem.name === CustomMenuItemNameEnum.CreateYourOwn) {
        allValid = !!twoSidedLineItem.selectedToppings && twoSidedLineItem.selectedToppings.length > 0;
        if (!allValid) return allValid;
    }
    if (twoSidedLineItem.name2 === CustomMenuItemNameEnum.CreateYourOwn) {
        allValid = !!twoSidedLineItem.selectedToppings2 && twoSidedLineItem.selectedToppings2.length > 0;
        if (!allValid) return allValid;
    }
    allValid = !!twoSidedLineItem.id && !!twoSidedLineItem.id2;
    return allValid;
};

export const mapToppingOptionsToToppings = (options: PizzaToppingOption[], pool: Topping[]): Topping[] =>
    options
        .filter((t) => t.selected)
        .flatMap((t) => {
            const match = pool.find((p) => p.name === t.name);
            if (!match) return [];
            const topping = new Topping(match);
            topping.selected = true;
            topping.multiplier = t.multiplier;
            topping.placement = t.placement;
            return [topping];
        });

export const mergeToppingOptions = (existing: Topping[], options: PizzaToppingOption[], pool: Topping[], placement?: PizzaSideEnum): Topping[] => {
    const untouched = existing.filter((t) => !options.some((p) => p.name === t.name));
    const modified = mapToppingOptionsToToppings(options, pool);
    if (placement !== undefined) modified.forEach((t) => (t.placement = placement));
    const combined = [...untouched, ...modified].sort((a, b) => {
        if (a.isMeat && !b.isMeat) return -1;
        if (!a.isMeat && b.isMeat) return 1;
        return a.sort - b.sort;
    });
    return combined;
};

export const mapHalfAndHalfToLineItem = (currentLineItem: LineItem, toppingsPool: Topping[], selection: TwoSidedSelection): LineItem => {
    const lineItem = new LineItem(currentLineItem);
    const isLeft = selection.side === PizzaSideEnum.left;

    if (selection.menuItem) {
        if (isLeft) {
            lineItem.id = selection.menuItem.itemExternalId;
            lineItem.name = scrubHtmlElements(selection.menuItem.name);
            lineItem.displayName = selection.menuItem.name;
            if (!selection.menuItem.isBuildYourOwn) lineItem.selectedToppings = [];
        } else {
            lineItem.id2 = selection.menuItem.itemExternalId;
            lineItem.name2 = scrubHtmlElements(selection.menuItem.name);
            lineItem.displayName2 = selection.menuItem.name;
            if (!selection.menuItem.isBuildYourOwn) lineItem.selectedToppings2 = [];
        }
    } else if (selection.selectedToppingOptions) {
        if (isLeft) {
            lineItem.selectedToppings = mergeToppingOptions(lineItem.selectedToppings, selection.selectedToppingOptions, toppingsPool, PizzaSideEnum.left);
        } else {
            lineItem.selectedToppings2 = mergeToppingOptions(lineItem.selectedToppings2 ?? [], selection.selectedToppingOptions, toppingsPool, PizzaSideEnum.right);
        }
    }

    lineItem.recipes2 = lineItem.recipes;
    return lineItem;
};

export const inlineToppingDisplay = (item) => {
    const prepend = item.multiplier > 1 ? `xtra ` : !item.selected ? 'No ' : '';
    return prepend + item.name;
};
export const inlineOrderHistory = (item: HistoryOrderItem) => {
    return `<b>${item.itemName}</b> <span class="normal x-small">(${item.size}${!item.crusttype?.includes('all') ? ' | ' + item.crusttype : ''})</span>`;
};

//#region Menu Item Price calculation
export const filterPricesBySize = (recipe, size, toppingPrices) =>
    toppingPrices?.filter((p) => (p.recipePid === recipe?.recipeId || p.recipePid == '-1') && (p.sizePid === size?.externalId || p.sizePid == '-1'));

export const matchToppingToPrice = (topping: Topping, toppingPrices: ToppingPrice[]) => {
    // Inefficient but this needs to prioritize a matching id first. If none match, fallback on the wildcard '-1' id
    let matches = toppingPrices.find((p) => p.toppingPid.some((tp) => tp === topping?.toppingPid));
    if (!matches) matches = toppingPrices.find((p) => p.toppingPid.some((tp) => tp === '-1'));
    return matches;
};
/* Must already have filtered down based on category, recipe, and size */
export const sumSelectedToppings = (toppings: Topping[], toppingPrices: ToppingPrice[]): number => {
    return toppings
        ?.filter((t) => t?.selected)
        .reduce((total, topping) => {
            const matches = matchToppingToPrice(topping, toppingPrices);
            const multiply = topping.isDefault && topping?.multiplier == 1 ? 0 : topping?.multiplier;
            return +(total += matches ? matches?.toppingprice * multiply : 0)?.toFixed(2);
        }, 0);
};
/**
 * @function sumToppingPriceBySelectedSize
 * @param {Recipe} recipe
 * @param {Size} size
 * @param {ToppingPrice[]} toppingPrices
 * @param {Topping[]} toppings
 * @param {boolean} includeBasePrice
 * @returns number
 */
export const sumToppingPriceBySelectedSize = (recipe?: Recipe, size?: Size, toppingPrices?: ToppingPrice[], toppings?: Topping[], includeBasePrice = true) => {
    if (!recipe || !size || !toppingPrices || !toppings) return 0;
    return +(sumSelectedToppings(toppings, filterPricesBySize(recipe, size, toppingPrices)) + (includeBasePrice ? size.price : 0)).toFixed(2);
};
//#endregion

/**
 * Increase `multiplier` and set `selected` for a random dressing in Topping[]
 * @param dressings
 * @param dressingCount
 * @param noToppingIndex - the index of the "No Dressing" option
 */
export const incrementRandomDressing = (dressings: Topping[], dressingCount: number, noToppingIndex: number) => {
    const randomIndex = Math.floor(Math.random() * dressingCount);
    if (randomIndex == noToppingIndex && dressings.length > 1) return incrementRandomDressing(dressings, dressingCount, noToppingIndex);
    else {
        dressings[randomIndex].multiplier += 1;
        dressings[randomIndex].selected = true;
    }
};

/**
 * Resets and selects multiple dressings with varying multipliers
 * @param dressings
 * @param preference - the "Default" dressing for a salad (Italian Garden == Italian)
 */
export const dressingVariety = (dressings: Topping[], preference?: Topping) => {
    const dressingCount = dressings.filter((d) => !d.isNoTopping).length;
    const noToppingIndex = dressings.findIndex((d) => d.isNoTopping);
    let numberToChange = 10;

    // set preferred dressing as selected with 5x multiplier
    if (preference) {
        const mainDressing = dressings.find((d) => d.toppingPid == preference.toppingPid);
        if (mainDressing) {
            mainDressing.multiplier = 5;
            mainDressing.selected = true;
            numberToChange = numberToChange - 5;
        }
    }

    for (let i = 0; i < numberToChange; i++) {
        incrementRandomDressing(dressings, dressingCount, noToppingIndex);
    }
};

export const buildOrderConfirmation = (requestOrder: FullOrder, orderResponse: OrderConfirmation): FullOrder => {
    requestOrder.orderHeaderID = orderResponse.orderHeaderID;
    requestOrder.orderHeader.submitResult.orderHeaderID = orderResponse.orderHeaderID;
    requestOrder.orderHeader.submitResult.promiseTime = DateTime.fromISO(`${requestOrder.logistics.date}T${requestOrder.logistics.time}`).plus({
        minutes: orderResponse?.promiseTime,
    });
    return requestOrder;
};

/** API */
export const fetchMenu = (id: string): Promise<Menu> => {
    return new Promise((resolve, reject) => {
        if (!id || id == '0') return reject();
        ApiHelper.get(`/menu?locationId=${id}`)
            .then((res) => resolve(new Menu(res)))
            .catch((error) => reject(error));
    });
};
let fetchingToppings = false;
export const fetchToppings = (queryObj: ToppingReq): Promise<AvailableTopping> => {
    if (fetchingToppings) return Promise.reject('Already fetching toppings');
    fetchingToppings = true;
    return new Promise((resolve, reject) => {
        ApiHelper.get(`/menutoppings?${objectToQueryString(queryObj)}`)
            .then((res) => resolve(new AvailableTopping(res)))
            .catch((error) => reject(error))
            .finally(() => (fetchingToppings = false));
    });
};
export const addToCart = (cartItem: LineItem, accountId = undefined): Promise<AddToCartRes> => {
    if (accountId) cartItem.customerId = accountId;
    return new Promise((resolve, reject) => {
        ApiHelper.post(`/addcartitem`, cartItem)
            .then((res) => resolve(new AddToCartRes({ ...res.orderDetails, promiseTime: res.orderHeader.submitResult.promiseTime.date })))
            .catch((error) => reject(error));
    });
};
/**
 * Add repeat order to cart using orderHeaderId & customerId
 * @param orderHeaderId string
 * @param customerId string
 * @returns AddToCartRes
 */
export const addOrderToCart = (orderHeaderId: string, customerId: string): Promise<AddToCartRes> => {
    return new Promise((resolve, reject) => {
        ApiHelper.post(`/addcartitem`, { customerId: customerId, orderHeaderId: orderHeaderId })
            .then((res) => resolve(new AddToCartRes({ ...res.orderDetails, promiseTime: res.orderHeader.submitResult.promiseTime.date })))
            .catch((error) => reject(error));
    });
};
export const updateCartItem = (cartItem: LineItem): Promise<AddToCartRes> => {
    return new Promise((resolve, reject) => {
        ApiHelper.post(`/updatecartitem`, cartItem)
            .then((res) => resolve(new AddToCartRes({ ...res.orderDetails, promiseTime: res.orderHeader.submitResult.promiseTime.date })))
            .catch((error) => reject(error));
    });
};
export const deleteFromCart = (deletePayload: any): Promise<AddToCartRes> => {
    return new Promise((resolve, reject) => {
        ApiHelper.post(`/deletecartitem`, {
            customerId: deletePayload.customerId,
            lineitemId: deletePayload.lineitemId,
        })
            .then((res) => resolve(new AddToCartRes({ ...res.orderDetails, promiseTime: res.orderHeader.submitResult.promiseTime.date })))
            .catch((error) => reject(error));
    });
};
export const changeOrderDetails = (fullOrder: FullOrder): Promise<AddToCartRes | any> => {
    const request = !fullOrder.logistics.customerId ? fullOrder : new OrderLogistics(fullOrder.logistics);
    return new Promise((resolve, reject) => {
        ApiHelper.post(`/changeorderdetails`, request)
            .then((res) => resolve(new AddToCartRes({ ...res.orderDetails, promiseTime: res.orderHeader.submitResult.promiseTime.date })))
            .catch((error) => reject(error));
    });
};

export const checkCouponCode = (code: string): Promise<Coupon> => {
    const { trackAddCoupon } = useGoogleAnalytics();
    return new Promise((resolve, reject) => {
        ApiHelper.get(`/checkcoupon?code=${code}`)
            .then((res) => {
                if (res.error) {
                    trackAddCoupon(code, false);
                    return reject(res);
                }
                trackAddCoupon(code, true);
                resolve(new Coupon(res));
            })
            .catch((error) => {
                trackAddCoupon(code, false);
                reject(error);
            });
    });
};
export const itemPrice = (item: LineItem): Promise<ItemPriceCal | null> => {
    return new Promise((resolve) =>
        ApiHelper.postLatest(`/getitemprice`, item)
            .then((res) => {
                const isResponseError = res?.[0]?.includes('0001');
                if (isResponseError) {
                    throw res[0];
                } else {
                    return res != null ? resolve(new ItemPriceCal(res)) : resolve(null);
                }
            })
            .catch((error) => {
                throw error;
            })
    );
};
export const orderPrice = (order: FullOrder): Promise<any> => {
    return ApiHelper.postLatest(`/getorderprice`, order);
};

export const submitOrder = (order: FullOrder, cvv?: string): Promise<OrderConfirmation> => {
    //@ts-ignore
    order.orderHeader.isMarketingEmail = (+order.orderHeader.isMarketingEmail).toString();

    const requestBody = cvv ? { ...order, cvv } : order;
    return new Promise((resolve, reject) => {
        ApiHelper.post(`/submitorder`, requestBody)
            .then((res) => {
                res instanceof Array && typeof res[0] === 'string' ? reject(phoenixError(res[0])) : resolve(new OrderConfirmation(res));
            })
            .catch((error) => reject(phoenixError(error)));
    });
};

export const validateGiftcard = (giftCard: GiftCard): Promise<GiftCard> => {
    return new Promise((resolve, reject) => {
        ApiHelper.post(`/validategiftcard`, giftCard.asValidationPayload)
            .then((res) => {
                res = new validGiftCardResponse(res);
                if (res.valid) {
                    giftCard.balance = res.balance;
                    resolve(giftCard);
                } else {
                    reject(res);
                }
            })
            .catch((error) => reject(error));
    });
};
export const fetchOrderDetail = (orderHeaderId: string): Promise<FullOrder> => {
    return new Promise((resolve, reject) => {
        ApiHelper.get(`/orderdetails?orderHeaderId=${orderHeaderId}`)
            .then((res) => {
                resolve(res);
                // resolve(new FullOrder(res));
            })
            .catch((error) => reject(error));
    });
};
export const replaceCart = (fullOrder: FullOrder): Promise<FullOrder> => {
    const customerId = fullOrder.orderHeader.customerId;
    fullOrder.orderDetails.lineItems.forEach((li) => (li.customerId = customerId));
    // eslint-disable-next-line
    return new Promise((resolve, reject) => {
        ApiHelper.post(`/replacecart`, fullOrder).then((order) => resolve(new FullOrder(order)));
    });
};
export const orderHistory = (customerId: string): Promise<HistoryOrder[]> => {
    // eslint-disable-next-line
    return new Promise((resolve, reject) => {
        ApiHelper.post(`/orderhistory?customer_id=${customerId}`).then((history) => resolve(history.map((h) => new HistoryOrder(h))));
    });
};
